import React from "react";
import { makeStyles } from "tss-react/mui";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CallIcon from "@mui/icons-material/Call";
import ReplyIcon from "@mui/icons-material/Reply";

export const getPreviewTextWithVariables = (text, vars, valores) => {
  if (!text) return "";
  return text.replace(/{{(\d+)}}/g, (match, numStr) => {
    const pos = parseInt(numStr, 10);
    const v = (vars || []).find((x) => Number(x.position) === pos);

    if (valores && valores[pos] !== undefined && valores[pos] !== "") {
      return valores[pos];
    }
    return v && v.exampleValue ? v.exampleValue : match;
  });
};

export const renderWhatsappText = (text) => {
  if (!text) return null;
  const inline = (line, li) => {
    const re = /```([^`]+)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;
    const out = [];
    let last = 0;
    let m;
    let k = 0;
    while ((m = re.exec(line))) {
      if (m.index > last) out.push(line.slice(last, m.index));
      if (m[1] != null) {
        out.push(
          <code key={`c${li}-${k}`} style={{ fontFamily: "monospace" }}>
            {m[1]}
          </code>
        );
      } else if (m[2] != null) {
        out.push(<strong key={`b${li}-${k}`}>{m[2]}</strong>);
      } else if (m[3] != null) {
        out.push(<em key={`i${li}-${k}`}>{m[3]}</em>);
      } else if (m[4] != null) {
        out.push(
          <span key={`s${li}-${k}`} style={{ textDecoration: "line-through" }}>
            {m[4]}
          </span>
        );
      }
      last = re.lastIndex;
      k += 1;
    }
    if (last < line.length) out.push(line.slice(last));
    return out;
  };
  return String(text)
    .split("\n")
    .map((line, li, arr) => (
      <React.Fragment key={li}>
        {inline(line, li)}
        {li < arr.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
};

export const previewButtonMeta = (type) => {
  switch ((type || "").toUpperCase()) {
    case "URL":
      return { Icon: OpenInNewIcon, fallback: "Visitar sitio web" };
    case "PHONE_NUMBER":
      return { Icon: CallIcon, fallback: "Llamar" };
    default:
      return { Icon: ReplyIcon, fallback: "Responder" };
  }
};

const useStyles = makeStyles()((theme) => ({
  previewWrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  },
  previewFrame: {
    marginTop: theme.spacing(1),
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    border:
      "1px solid " +
      (theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "#e2e2e2"),
    boxShadow: "0 2px 10px rgba(0,0,0,0.14)"
  },
  previewTopBar: {
    backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    fontWeight: 600,
    fontSize: 14,
    padding: "12px 16px"
  },
  previewWallpaper: {
    flex: 1,
    minHeight: 200,
    backgroundColor: theme.palette.mode === "dark" ? "#0b141a" : "#efeae2",
    backgroundImage:
      theme.palette.mode === "dark"
        ? "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.035) 1px, transparent 1px)",
    backgroundSize: "18px 18px",
    padding: theme.spacing(2),
    overflowY: "auto"
  },
  previewBubble: {
    position: "relative",
    backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    borderRadius: 8,
    borderTopLeftRadius: 0,
    maxWidth: "88%",
    fontSize: 13.5,
    lineHeight: 1.42,
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    wordBreak: "break-word",
    overflow: "hidden"
  },
  previewBubbleContent: {
    padding: "7px 9px 4px 9px"
  },
  previewMedia: {
    height: 130,
    borderRadius: 6,
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#54656f",
    backgroundColor: theme.palette.mode === "dark" ? "#111b21" : "#e9edef",
    objectFit: "cover",
    width: "100%"
  },
  bubbleHeader: {
    fontWeight: 700,
    marginBottom: 4,
    whiteSpace: "pre-wrap"
  },
  bubbleBody: {
    whiteSpace: "pre-wrap"
  },
  bubbleFooter: {
    marginTop: 6,
    fontSize: 12,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#667781",
    whiteSpace: "pre-wrap"
  },
  bubbleTime: {
    textAlign: "right",
    fontSize: 11,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#667781",
    marginTop: 2,
    lineHeight: 1
  },
  previewButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    color: "#00a5f4",
    fontSize: 13.5,
    fontWeight: 500,
    padding: "9px 8px",
    borderTop:
      "1px solid " +
      (theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "#e9edef"),
    "& svg": { fontSize: 17 }
  }
}));

const TemplatePreview = ({
  template = {},
  variables = [],
  buttons = [],
  valores = null,
  titulo = "Tu plantilla",
  vacio = "Escribe el cuerpo del mensaje…"
}) => {
  const { classes } = useStyles();

  const headerType = template.headerFormat;
  const isTextHeader = headerType === "TEXT";
  const isMediaHeader =
    headerType === "IMAGE" ||
    headerType === "VIDEO" ||
    headerType === "DOCUMENT" ||
    headerType === "LOCATION";

  const headerText = isTextHeader
    ? getPreviewTextWithVariables(template.headerText || "", variables, valores)
    : "";

  const mediaLabel =
    headerType === "IMAGE"
      ? { emoji: "🖼️", text: "Imagen" }
      : headerType === "VIDEO"
      ? { emoji: "🎬", text: "Video" }
      : headerType === "DOCUMENT"
      ? { emoji: "📄", text: "Documento" }
      : { emoji: "📍", text: "Ubicación" };

  const previewBodyText = getPreviewTextWithVariables(
    template.bodyText || vacio,
    variables,
    valores
  );

  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;

  return (
    <div className={classes.previewWrapper}>
      <div className={classes.previewFrame}>
        <div className={classes.previewTopBar}>{titulo}</div>
        <div className={classes.previewWallpaper}>
          <div className={classes.previewBubble}>
            <div className={classes.previewBubbleContent}>
              {isMediaHeader && (
                <div className={classes.previewMedia}>
                  <span style={{ fontSize: 26 }}>{mediaLabel.emoji}</span>
                  <span>{mediaLabel.text}</span>
                </div>
              )}

              {isTextHeader && headerText && (
                <div className={classes.bubbleHeader}>
                  {renderWhatsappText(headerText)}
                </div>
              )}

              <div className={classes.bubbleBody}>
                {renderWhatsappText(previewBodyText)}
              </div>

              {template.footerText && (
                <div className={classes.bubbleFooter}>
                  {template.footerText}
                </div>
              )}

              <div className={classes.bubbleTime}>{time}</div>
            </div>

            {(buttons || []).map((btn, idx) => {
              const { Icon, fallback } = previewButtonMeta(btn.type);
              return (
                <div key={idx} className={classes.previewButton}>
                  <Icon />
                  <span>{btn.text || fallback}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
